import { addStoreLocaleByCookie } from 'lib/storeLocale/client'
import { updateAppUrl } from 'lib/url/route'
import { stringToQueryObject, objectToQueryString } from 'lib/helpers'
import { NavigationLinks, NavigationLinksArray } from './types'
import { handleScrollToTop } from 'components/SA/SearchProvider/helpers'
import { LocaleCurrency } from 'types/Shared/Locale'
import { formatPrice, convertDefaultToLocaleCurrency } from 'components/FormatPrice/helpers'

const BROWSE = `/public/browse`
const END_OF_YEAR_2025_SALE_START = 1766736000000 // Dec 26, 2025 00:00:00 PST
const END_OF_YEAR_2025_SALE_END = 1767340800000 // Jan 2, 2026 00:00:00 PST

// const COLLECTIONS = `/saatchi/browse2`
// const USER_ARTWORKS = `/saatchi/browse2`
const defaultSearch = (query: string) => {
  const location = addStoreLocaleByCookie(`/all?query=${encodeURIComponent(query)}`)
  window.location.href = location
}

// Custom Search Logic
const CUSTOM_SEARCH = [
  {
    page: BROWSE,
    delayed: true,
    action: (query: string): void => {
      const { pathname, search } = window.location
      // We NEED to break down search and merge/overwrite QUERY
      const queryObject = stringToQueryObject(search)
      const href = `${pathname}?${objectToQueryString({
        ...queryObject,
        page: '',
        // Null values will be stripped, RESULT page=1
        query,
      })}`
      updateAppUrl({
        app: BROWSE,
        href,
      })
    },
  },
]

const getCurrentPagePath = () => {
  const { url: currentPagePath } = window.history.state || {}

  // BlueCore from GTM is overridden the window.history.state
  return currentPagePath || BROWSE
}

const checkForCustomSearch = (): string => {
  const currentPagePath = getCurrentPagePath()
  // Grab all PAGES
  const pagesAvailable: Array<string> = CUSTOM_SEARCH.map(({ page }) => page)

  // Check if any match
  const currentPage = pagesAvailable.find((page) => currentPagePath.search(page) > -1)
  return currentPage || 'unknownPage'
}

const getCustomSearchAction = (currentPage: string) => {
  const searchAction = CUSTOM_SEARCH.find(({ page }) => page === currentPage)
  return searchAction ? searchAction.action : null
}

// Custom Timer Logic for Search Input Field Updates
let timer
const clearTimer = () => {
  clearTimeout(timer)
  timer = null
}

export const submitSearch = (query: string): void => {
  // If USER clicks enter, clear out any previous DELAYED searches
  clearTimer()
  // Make sure we do NOT have custom logic for a NEXT page
  const currentPage = checkForCustomSearch()

  if (currentPage) {
    const searchAction = getCustomSearchAction(currentPage)
    if (searchAction) {
      handleScrollToTop(() => searchAction(query))
      return
    }
  }

  defaultSearch(query)
}

const isEndOfYearSalePeriod = (): boolean => {
  const now = Date.now()
  return now >= END_OF_YEAR_2025_SALE_START && now < END_OF_YEAR_2025_SALE_END
}

export const getCuratedDealsNavTitle = (): string => {
  return isEndOfYearSalePeriod() ? 'End of Year Sale' : 'Curated Deals'
}

export const transformNavigationLinks = ({
  navigationLinks,
  localeCurrency,
}: {
  navigationLinks: NavigationLinksArray
  localeCurrency: LocaleCurrency
}): NavigationLinksArray => {
  const curatedDealsTitle = getCuratedDealsNavTitle()
  const price500 = formatPrice(convertDefaultToLocaleCurrency(500, localeCurrency), localeCurrency)
  const price1000 = formatPrice(
    convertDefaultToLocaleCurrency(1000, localeCurrency),
    localeCurrency
  )

  const result: NavigationLinks[] = []

  for (const category of navigationLinks) {
    // Fix Prints material URL param typo
    if (category.title === 'Prints') {
      result.push({
        ...category,
        links: category.links.map((section) => {
          if (section.title !== 'Material') return section
          return {
            ...section,
            links: section.links.map((link) => ({
              ...link,
              url: link.url.replace('prints_materials=', 'print_materials='),
            })),
          }
        }),
      })
      continue
    }

    // Rename "Curated Deals" during End of Year Sale period
    if (category.title === 'Curated Deals') {
      result.push({ ...category, title: curatedDealsTitle })
      continue
    }

    // Update New Arrivals price labels with locale currency
    if (category.title === 'New Arrivals') {
      result.push({
        ...category,
        links: category.links.map((section) => ({
          ...section,
          links: section.links.map((link) => {
            if (link.url === '/all?geo_prices=0-500&sort=newest') {
              return { ...link, title: `New Art Under ${price500}` }
            }
            if (link.url === '/all?geo_prices=0-1000&sort=newest') {
              return { ...link, title: `New Art Under ${price1000}` }
            }
            return link
          }),
        })),
      })
      continue
    }

    result.push(category)
  }

  return result
}
